/**
 * optionStrategyLegs — pure helpers that turn user-picked underlying levels into
 * concrete option legs for the 4-quad "click-to-build" basket flow.
 *
 * No React / no I/O: this is unit-testable and shared by the chart's strategy
 * capture flow and (indirectly) the review dialog. Leg shape matches the backend
 * preview contract (optionsSuggester.previewFromLegs): action BUY|SELL,
 * optionType CE|PE, strike, expiry (YYYY-MM-DD).
 */

export type LegAction = 'BUY' | 'SELL';
export type OptType = 'CE' | 'PE';
export interface StrategyLeg { action: LegAction; optionType: OptType; strike: number; expiry: string; }

/** How many clicks each Build button needs + the per-click role text (for the banner). */
export interface PickSpec { clicks: number; roles: string[]; }

export const STRATEGY_PICKS: Record<string, PickSpec> = {
  // Both legs SELL at the same strike — one click marks the expected pin.
  short_straddle:   { clicks: 1, roles: ['expected expiry / pin level'] },
  // Sell OTM call (upper) + sell OTM put (lower).
  short_strangle:   { clicks: 2, roles: ['upper (call) strike', 'lower (put) strike'] },
  // Bear call credit: sell near call, buy protective higher call.
  bear_call_spread: { clicks: 2, roles: ['short call (price won’t breach UP)', 'protective higher call'] },
  // Bull put credit: sell near put, buy protective lower put.
  bull_put_spread:  { clicks: 2, roles: ['short put (price won’t breach DOWN)', 'protective lower put'] },
  // Bull call debit: buy lower call, sell higher call.
  bull_call_spread: { clicks: 2, roles: ['long call (lower)', 'short call (higher)'] },
  // Bear put debit: buy higher put, sell lower put.
  bear_put_spread:  { clicks: 2, roles: ['long put (higher)', 'short put (lower)'] },
  // Naked long option — one click marks the strike to BUY (item 12).
  long_call: { clicks: 1, roles: ['call strike to BUY'] },
  long_put:  { clicks: 1, roles: ['put strike to BUY'] },
  // Iron Condor: sell OTM call + buy higher call (call wing); sell OTM put + buy
  // lower put (put wing). Net credit, defined risk. 4 strikes: k1>k0>k2>k3.
  iron_condor: { clicks: 4, roles: ['short call (sell, upper)', 'protective call (buy, higher)', 'short put (sell, lower)', 'protective put (buy, lowest)'] },
  // Iron Fly: sell ATM call + ATM put (the pin), buy an upper call wing + lower put
  // wing. Higher credit, narrower profit zone. 3 picks: pin, upper wing, lower wing.
  iron_fly: { clicks: 3, roles: ['ATM / pin (sell CE & PE)', 'upper protective call (buy)', 'lower protective put (buy)'] },
};

/**
 * Strategies whose strikes are chosen AUTOMATICALLY by the backend (no click-to-pick
 * capture) — e.g. the premium-matched 1×2 ratio back-spreads (SELL 1 ATM + BUY 2 of the
 * nearest OTM strike priced ≤ ½ the ATM premium). The Build chip opens the review dialog
 * directly with no seed legs; the backend previewBasket(strategyId) returns the concrete
 * legs. These have NO entry in STRATEGY_PICKS by design.
 */
export const AUTO_BUILD_STRATEGIES = new Set<string>(['call_backspread_pm', 'put_backspread_pm']);

/** Nearest available strike to a clicked price (null if no strikes). */
export function snapToStrike(price: number, strikes: number[]): number | null {
  if (!strikes.length || !Number.isFinite(price)) return null;
  let best = strikes[0];
  let bestD = Math.abs(strikes[0] - price);
  for (const s of strikes) {
    const d = Math.abs(s - price);
    if (d < bestD) { best = s; bestD = d; }
  }
  return best;
}

/**
 * Sign of a leg's premium in the synthetic "net premium" combined price:
 * a SELL leg adds the credit received (+1); a BUY leg subtracts the debit paid (−1).
 * So combined = Σ comboSign(leg) × legPrice → credit strategies are positive,
 * debit strategies negative, and the value shrinks toward 0 as a credit position
 * profits.
 */
export function comboSign(action: LegAction): 1 | -1 { return action === 'SELL' ? 1 : -1; }

/**
 * Parse a Delta option symbol `{C|P}-{BASE}-{STRIKE}-{DDMMYY}`
 * (e.g. `C-BTC-67000-160626`) into structured leg metadata. Returns null if the
 * symbol isn't in that shape (e.g. an NSE leg), so callers can fall back.
 */
export function parseDeltaOptionSymbol(sym: string): { optionType: OptType; base: string; strike: number; expiryISO: string } | null {
  if (!sym) return null;
  const m = /^([CP])-([A-Z0-9]+)-(\d+(?:\.\d+)?)-(\d{2})(\d{2})(\d{2})$/.exec(sym.trim().toUpperCase());
  if (!m) return null;
  const [, cp, base, strikeStr, dd, mm, yy] = m;
  const strike = Number(strikeStr);
  if (!Number.isFinite(strike)) return null;
  const expiryISO = `20${yy}-${mm}-${dd}`;
  return { optionType: cp === 'C' ? 'CE' : 'PE', base, strike, expiryISO };
}

export interface BuildResult { ok: boolean; legs?: StrategyLeg[]; reason?: string }

/**
 * Map sequential picked levels → concrete legs for a strategy, snapping each pick
 * to the nearest strike and enforcing the role ordering (so e.g. a Call-credit
 * protective leg can never sit below the short leg). Returns a clear reason on
 * rejection so the caller can keep the capture armed for a re-pick.
 */
export function buildLegsFromPicks(strategyId: string, picks: number[], strikes: number[], expiry: string): BuildResult {
  const spec = STRATEGY_PICKS[strategyId];
  if (!spec) return { ok: false, reason: `Unknown strategy ${strategyId}` };
  if (!expiry) return { ok: false, reason: 'Select an expiry first' };
  if (!strikes.length) return { ok: false, reason: 'Option chain not loaded yet' };
  if (picks.length < spec.clicks) return { ok: false, reason: 'Not enough levels picked' };

  const snapped = picks.slice(0, spec.clicks).map(p => snapToStrike(p, strikes));
  if (snapped.some(s => s == null)) return { ok: false, reason: 'Could not map a click to a strike' };
  const k = snapped as number[];
  const leg = (action: LegAction, optionType: OptType, strike: number): StrategyLeg => ({ action, optionType, strike, expiry });

  switch (strategyId) {
    case 'short_straddle':
      return { ok: true, legs: [leg('SELL', 'CE', k[0]), leg('SELL', 'PE', k[0])] };

    case 'short_strangle':
      if (k[0] === k[1]) return { ok: false, reason: 'Pick distinct upper (call) and lower (put) strikes' };
      if (k[0] < k[1]) return { ok: false, reason: 'First click (call) must be ABOVE the second click (put)' };
      return { ok: true, legs: [leg('SELL', 'CE', k[0]), leg('SELL', 'PE', k[1])] };

    case 'bear_call_spread':
      if (k[1] <= k[0]) return { ok: false, reason: 'Protective call must be ABOVE the short call — pick a higher second level' };
      return { ok: true, legs: [leg('SELL', 'CE', k[0]), leg('BUY', 'CE', k[1])] };

    case 'bull_put_spread':
      if (k[1] >= k[0]) return { ok: false, reason: 'Protective put must be BELOW the short put — pick a lower second level' };
      return { ok: true, legs: [leg('SELL', 'PE', k[0]), leg('BUY', 'PE', k[1])] };

    case 'bull_call_spread':
      if (k[1] <= k[0]) return { ok: false, reason: 'Sell call must be ABOVE the buy call — pick a higher second level' };
      return { ok: true, legs: [leg('BUY', 'CE', k[0]), leg('SELL', 'CE', k[1])] };

    case 'bear_put_spread':
      if (k[1] >= k[0]) return { ok: false, reason: 'Sell put must be BELOW the buy put — pick a lower second level' };
      return { ok: true, legs: [leg('BUY', 'PE', k[0]), leg('SELL', 'PE', k[1])] };

    case 'long_call':
      return { ok: true, legs: [leg('BUY', 'CE', k[0])] };

    case 'long_put':
      return { ok: true, legs: [leg('BUY', 'PE', k[0])] };

    case 'iron_condor': {
      const [sc, lc, sp, lp] = k; // short call, long call, short put, long put
      if (!(lc > sc)) return { ok: false, reason: 'Protective call (2nd) must be ABOVE the short call (1st)' };
      if (!(sp < sc)) return { ok: false, reason: 'Short put (3rd) must be BELOW the short call (1st)' };
      if (!(lp < sp)) return { ok: false, reason: 'Protective put (4th) must be BELOW the short put (3rd)' };
      return { ok: true, legs: [leg('SELL', 'CE', sc), leg('BUY', 'CE', lc), leg('SELL', 'PE', sp), leg('BUY', 'PE', lp)] };
    }

    case 'iron_fly': {
      const [atm, lc, lp] = k; // pin (ATM shorts), upper call wing, lower put wing
      if (!(lc > atm)) return { ok: false, reason: 'Upper protective call (2nd) must be ABOVE the ATM/pin (1st)' };
      if (!(lp < atm)) return { ok: false, reason: 'Lower protective put (3rd) must be BELOW the ATM/pin (1st)' };
      return { ok: true, legs: [leg('SELL', 'CE', atm), leg('SELL', 'PE', atm), leg('BUY', 'CE', lc), leg('BUY', 'PE', lp)] };
    }

    default:
      return { ok: false, reason: `Unsupported strategy ${strategyId}` };
  }
}
